// A2F-764: section focus state + ribbon-button highlight binding +
// throttled telemetry.
//
// One hook owns three concerns because they're inseparable:
//   1. The currently focused section id (drives LIVE PREVIEW pill).
//   2. The ribbon button to ring-pulse (drives FauxWordRibbon).
//   3. Throttled `onboarding_section_focused` event — fires at most
//      once per section_id per page-view (per Brian's note: without
//      throttling this fires dozens of times per visit).
//
// The hook does NOT track which configurator sub-tab (Headings/Body/
// Captions/Lists) is active inside the formatting section — that's
// owned by the section component because it's local UI state. Instead
// the section component calls `setFocus('formatting', subTab)` whenever
// the sub-tab changes, and the hook maps section + subTab → ribbon id.

import { useCallback, useRef, useState } from 'react'
import { Telemetry } from '../../lib/telemetry'
import type { OnboardingSectionFocusedProps } from '../../lib/telemetry-types'
import type { RibbonButtonId } from './FauxWordRibbon'

export type SectionId = OnboardingSectionFocusedProps['section_id']
export type FormattingSubTab = 'headings' | 'body' | 'captions' | 'lists'

interface FocusState {
  section: SectionId | null
  ribbonId: RibbonButtonId | null
}

// Section → ribbon button. Sub-tabs override this when the section is
// 'formatting'. Sections without a 1:1 ribbon button (doctype, install)
// use null — the ribbon goes idle (no ring) but the LIVE PREVIEW pill
// still shows the section label.
//
// Packs → 'mychrona' (not 'sections') because 3 of 4 packs surface in
// the My Chrona taskpane (Cover Letter Validation, FDA terminology
// coming-soon, eCTD formatting coming-soon). Only the IND Section
// Headings pack maps to its own ribbon button — and that one is
// locked-on / non-toggleable, so it's not what users edit when they
// open the Packs section. Ringing My Chrona points at where most
// pack output actually shows up.
const SECTION_TO_RIBBON: Record<SectionId, RibbonButtonId | null> = {
  doctype: null,
  formatting: 'numbered',
  tables: 'table',
  packs: 'mychrona',
  rules: 'mychrona',
  install: null,
}

// Formatting sub-tab → ribbon button. The 'lists' sub-tab maps to the
// Indentation Menu — there is no standalone "Lists" button in the
// production ribbon (A2F-610 retired the Lists group). The lists
// sub-tab in v1's FIELD_GROUPS is display-only anyway, so wiring it
// to Indentation keeps the ring meaningful even though no field
// changes the user can make.
const FORMATTING_SUB_TO_RIBBON: Record<FormattingSubTab, RibbonButtonId> = {
  headings: 'numbered',
  body: 'body',
  captions: 'caption',
  lists: 'indentation',
}

export function useFocusedSection(): {
  section: SectionId | null
  ribbonId: RibbonButtonId | null
  setFocus: (section: SectionId | null, subTab?: FormattingSubTab) => void
} {
  const [state, setState] = useState<FocusState>({ section: null, ribbonId: null })
  // Page-lifetime ref. Survives blur/refocus cycles; clears only on
  // unmount. That's the contract the user asked for: one fire per
  // section per session.
  const seenRef = useRef<Set<SectionId>>(new Set())

  const setFocus = useCallback(
    (section: SectionId | null, subTab?: FormattingSubTab) => {
      let ribbonId: RibbonButtonId | null = null
      if (section === 'formatting' && subTab) {
        ribbonId = FORMATTING_SUB_TO_RIBBON[subTab]
      } else if (section) {
        ribbonId = SECTION_TO_RIBBON[section]
      }
      setState({ section, ribbonId })

      if (section && !seenRef.current.has(section)) {
        seenRef.current.add(section)
        Telemetry.onboardingSectionFocused({ section_id: section })
      }
    },
    [],
  )

  return { section: state.section, ribbonId: state.ribbonId, setFocus }
}
